'use client';

import React, { useState, useEffect, useRef } from "react";
import { Sun, Moon, Terminal, Lock, KeyRound, CheckCircle2, XCircle } from 'lucide-react';

const SECRET_PHRASES = {
  rich: { title: "Dev Team Capital Requirements", message: "This pipeline is completely frozen pending development budget allocation." },
  money: { title: "Commercial Authorization Needed", message: "We require professional clearance from the accounts department to lift the restriction." },
  school: { title: "Cat & Mouse Architecture", message: "Every countermeasure is accounted for. We engineered the architecture." },
  help: { title: "System Status Query", message: "Reach out to the core developers directly to settle outstanding milestones." },
  easter: { title: "Easter Eggs", message: "These are the easter eggs \' rich, money, school, help, easter, fixit, peace, bag'. "},
  fixit: { title: "Resolution Protocol", message: "We want to deploy features, but infrastructure demands clearing the ledger first." },
  peace: { title: "Operational Compromise", message: "Provide the agreed-upon development framework, and we will restore live status." },
  bag: { title: "💼 OVERRIDE DISPATCHED", message: "Zero deployment velocity until the development fund is fully settled." },
};

export default function MaintenancePage() {
  const [typed, setTyped] = useState("");
  const [activeMessage, setActiveMessage] = useState(null);
  const [konamiProgress, setKonamiProgress] = useState(0);
  const [clickCount, setClickCount] = useState(0);
  const [isDark, setIsDark] = useState(true);
  
  // Interactive States
  const [overrideInput, setOverrideInput] = useState("");
  const [overrideStatus, setOverrideStatus] = useState("idle"); // idle, success, error

  const moneyRef = useRef(null);
  const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight'];

  const toggleTheme = () => setIsDark(!isDark);

  // Keyboard Handler for Easter Eggs
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent triggers while typing in the input box
      if (document.activeElement.tagName === 'INPUT') return;

      // Konami Logic
      if (e.key === konamiCode[konamiProgress]) {
        const newProgress = konamiProgress + 1;
        setKonamiProgress(newProgress);
        if (newProgress === konamiCode.length) {
          setActiveMessage({
            title: "🔓 MASTER CONTROL BYPASS",
            message: "Developer payload unlocked. Systems standby for manual initialization.",
          });
          setKonamiProgress(0);
        }
      } else {
        setKonamiProgress(0);
      }

      // Secret Phrase Logic
      const char = e.key.toLowerCase();
      if (char.length === 1 && /[a-z]/.test(char)) {
        const newTyped = (typed + char).slice(-10);
        setTyped(newTyped);

        const foundKey = Object.keys(SECRET_PHRASES).find(key => newTyped.endsWith(key));
        if (foundKey) {
          setActiveMessage({ ...SECRET_PHRASES[foundKey] });
          setTyped("");
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [konamiProgress, typed]);

  const handleBriefcaseClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    if (newCount === 7) {
      setActiveMessage(SECRET_PHRASES.bag);
      setClickCount(0);
    }
  };

  const handleOverrideSubmit = (e) => {
    e.preventDefault();
    if (overrideInput.toLowerCase() === "devteam_s4_override") {
      setOverrideStatus("success");
      setTimeout(() => {
        alert("SUCCESS: Platform authorized. Simulating unlock mechanism.");
        setOverrideStatus("idle");
        setOverrideInput("");
      }, 1200);
    } else {
      setOverrideStatus("error");
      setTimeout(() => setOverrideStatus("idle"), 2000);
    }
  };

  const closeMessage = () => {
    setActiveMessage(null);
    setClickCount(0);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#040711]' : 'bg-gray-50'} flex items-center justify-center text-white overflow-hidden relative transition-all duration-700`}>
      
      {/* Dynamic Style for Shake Animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        .animate-shake {
          animation: shake 0.15s ease-in-out 0s 2;
        }
      `}</style>

      {/* Cyber-Grid Overlay */}
      <div className={`absolute inset-0 bg-[linear-gradient(to_right,#14192d_1px,transparent_1px),linear-gradient(to_bottom,#14192d_1px,transparent_1px)] bg-[size:40px_40px] ${isDark ? 'opacity-30' : 'opacity-5'} z-0`}></div>
      
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/5 blur-[120px] rounded-full pointer-events-none z-10"></div>

      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className={`absolute top-6 right-6 z-50 p-4 rounded-2xl border active:scale-95 transition-all flex items-center justify-center ${isDark ? 'bg-slate-900/50 border-white/5 hover:bg-slate-900' : 'bg-white/80 border-gray-200 hover:bg-white text-gray-900 shadow-md'}`}
      >
        {isDark ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} className="text-slate-600" />}
      </button>

      {/* Main Container */}
      <div className={`relative z-20 text-center px-8 py-10 max-w-md backdrop-blur-3xl border rounded-[2rem] shadow-2xl transition-all duration-500 ${isDark ? 'bg-slate-950/60 border-white/10 text-white shadow-blue-950/20' : 'bg-white/80 border-gray-200 text-gray-900 shadow-xl'}`}>
        
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest bg-red-500/10 text-red-400 border border-red-500/20 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
          <span>Developer Lockout Active</span>
        </div>

        <div
          ref={moneyRef}
          className="mb-4 text-6xl cursor-pointer transition-all duration-500 select-none filter drop-shadow-md hover:scale-105 active:scale-95"
          onClick={handleBriefcaseClick}
        >
          💼
        </div>

        <h1 className="text-2xl font-black mb-3 tracking-tight leading-tight uppercase font-mono">
          Instance Suspended By Devs
        </h1>

        <p className={`text-sm mb-6 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
          This platform's source code deployment has been manually isolated by the engineering team until formal development outlays are cleared.
        </p>

        {/* Diagnostics Terminal */}
        <div className={`text-left font-mono text-[11px] p-4 rounded-2xl border flex flex-col gap-1.5 ${isDark ? 'bg-black/50 border-white/5 text-slate-400' : 'bg-slate-100 border-gray-200 text-slate-600'}`}>
          <div className="flex items-center gap-1.5 font-bold mb-1 opacity-80 text-xs">
            <Terminal size={12} className="text-blue-400" />
            <span>CORE_RESTRICTION_LOG</span>
          </div>
          <div className="flex justify-between border-b border-white/5 pb-1 mb-1">
            <span>&gt; INTERCEPT:</span>
            <span className="text-red-400 font-bold flex items-center gap-1">MANUAL_LOCK <Lock size={10}/></span>
          </div>
          <div className="flex justify-between">
            <span>&gt; INITIATOR:</span>
            <span className="text-blue-400 font-bold">DEVS_S4_B&V</span>
          </div>
          <div className="flex justify-between">
            <span>&gt; PIPELINE:</span>
            <span className="opacity-70">RE-DEPLOYMENT_FROZEN</span>
          </div>
        </div>

        {/* Override Form */}
        <form onSubmit={handleOverrideSubmit} className="mt-4 text-left font-mono text-[11px]">
          <label className={`block mb-1.5 text-[10px] uppercase tracking-wider font-bold opacity-70 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            System Override Input
          </label>
          <div className="relative flex items-center">
            <KeyRound size={12} className="absolute left-3.5 text-slate-500" />
            <input 
              type="password"
              placeholder="Enter core authentication sequence..."
              value={overrideInput}
              onChange={(e) => setOverrideInput(e.target.value)}
              className={`w-full py-2.5 pl-9 pr-10 rounded-xl text-[11px] font-mono outline-none border transition-all duration-300 ${
                isDark 
                  ? 'bg-slate-900/60 border-white/5 text-slate-200 placeholder-slate-600 focus:border-blue-500/40 focus:bg-slate-900' 
                  : 'bg-white border-gray-200 text-slate-900 placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
              }`}
            />
            <div className="absolute right-3">
              {overrideStatus === "idle" && <span className="text-slate-600 text-[9px] uppercase font-bold animate-pulse">Waiting</span>}
              {overrideStatus === "success" && <CheckCircle2 size={14} className="text-green-400" />}
              {overrideStatus === "error" && <XCircle size={14} className="text-red-400 animate-shake" />}
            </div>
          </div>
        </form>

        <div className="text-[9px] tracking-widest text-slate-500 mt-6 font-mono uppercase">
          Build Signature: WEBDEV_HLBY // ENGR_VER_3.0
        </div>
      </div>

      {/* Security Modal */}
      {activeMessage && (
        <div
          className="fixed inset-0 bg-[#02040a]/95 backdrop-blur-md flex items-center justify-center z-[9999] text-center px-6 cursor-pointer"
          onClick={closeMessage}
        >
          <div className="max-w-md w-full bg-slate-950 border border-white/10 rounded-[2rem] p-10 text-white font-mono shadow-2xl shadow-blue-950/40">
            <div className="inline-flex p-4 rounded-2xl bg-blue-500/10 text-blue-400 border border-blue-500/20 mb-4">
              <Terminal size={32} />
            </div>
            <h2 className="text-lg font-black uppercase tracking-wider text-blue-400 mb-3">
              {activeMessage.title}
            </h2>
            <p className="text-xs text-slate-400 leading-relaxed max-w-xs mx-auto">
              {activeMessage.message}
            </p>
            <div className="mt-8 pt-4 border-t border-white/5 text-[10px] text-slate-500 animate-pulse">
              [ Click anywhere to reset security state ]
            </div>
          </div>
        </div>
      )}
    </div>
  );
}